import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Trash2, User, BookOpen, RotateCcw } from 'lucide-react';
import { useMarkingStore } from '../../stores/markingStore';
import { useAuthStore } from '../../stores/authStore';

const STATUS_CONFIG = {
  uploading:       { label: 'Uploading',      dot: 'bg-blue-400',   pill: 'bg-blue-50 text-blue-700 ring-blue-200' },
  processing:      { label: 'Processing',     dot: 'bg-blue-400',   pill: 'bg-blue-50 text-blue-700 ring-blue-200' },
  'manual-review': { label: 'Manual Review',  dot: 'bg-indigo-400', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  'ai-review':     { label: 'AI Review',      dot: 'bg-indigo-400', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  analyzing:       { label: 'Analyzing',      dot: 'bg-purple-400', pill: 'bg-purple-50 text-purple-700 ring-purple-200' },
  overlaying:      { label: 'Overlaying',     dot: 'bg-orange-400', pill: 'bg-orange-50 text-orange-700 ring-orange-200' },
  completed:       { label: 'Completed',      dot: 'bg-green-500',  pill: 'bg-green-50 text-green-700 ring-green-200' },
  failed:          { label: 'Failed',         dot: 'bg-red-500',    pill: 'bg-red-50 text-red-700 ring-red-200' }
};

const MarkingJobCard = ({ job }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isCreatingModeration, setIsCreatingModeration] = useState(false);
  const [moderationError, setModerationError] = useState('');
  const { deleteMarkingJob, createModerationJob } = useMarkingStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const statusInfo = job.status === 'ai-review' ? { ...STATUS_CONFIG['ai-review'], label: {
    preparation: 'Evidence review', calibration: 'Calibration', 'question-review': 'Question review',
    moderation: 'In moderation', 'ready-to-release': 'Ready to release'
  }[job.aiWorkflow?.stage] || 'AI Review' } : STATUS_CONFIG[job.status] || STATUS_CONFIG['processing'];
  const isActive = ['uploading', 'processing', 'analyzing', 'overlaying', 'manual-review'].includes(job.status);
  const results = job.results;

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getScoreColor = (pct) => {
    if (pct >= 70) return 'text-green-600 bg-green-50 ring-green-200';
    if (pct >= 50) return 'text-yellow-600 bg-yellow-50 ring-yellow-200';
    return 'text-red-600 bg-red-50 ring-red-200';
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteMarkingJob(job.id, user?.uid);
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setConfirmDelete(false);
  };

  const handleCancelDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDelete(false);
  };

  const handleCreateModeration = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user?.uid || isCreatingModeration) return;
    setIsCreatingModeration(true);
    setModerationError('');
    try {
      const newJobId = await createModerationJob(job, user.uid);
      navigate(`/marking/${newJobId}`);
    } catch (error) {
      setModerationError(error?.message || 'Failed to create the moderation job.');
    } finally {
      setIsCreatingModeration(false);
    }
  };

  return (
    <Link
      to={`/marking/${job.id}`}
      className="group block bg-white rounded-lg border border-gray-200 hover:border-sigma-300 hover:shadow-md transition-all duration-200 hover:border-l-sigma-500 hover:border-l-4"
    >
      <div className="flex items-center px-4 py-4 gap-4">
        {/* Status dot */}
        <div className="flex-shrink-0">
          <span className={`block w-3 h-3 rounded-full ${statusInfo.dot} ${isActive ? 'animate-pulse' : ''}`} />
        </div>

        {/* Center: info stack */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate text-sm">
            {job.title || 'Untitled Marking'}
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
            {job.subject && (
              <span className="flex items-center">
                <BookOpen className="w-3 h-3 mr-1" />
                {job.subject}{job.grade ? ` - Grade ${job.grade}` : ''}
              </span>
            )}
            {job.learnerName && (
              <span className="flex items-center">
                <User className="w-3 h-3 mr-1" />
                {job.learnerName}{job.learnerId ? ` (${job.learnerId})` : ''}
              </span>
            )}
            {job.markerName && (
              <span>Marker: {job.markerName}</span>
            )}
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {formatDate(job.createdAt)}
            </span>
          </div>

          {/* Progress bar for active jobs */}
          {isActive && job.progress && job.progress.totalPages > 0 && (
            <div className="mt-2">
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-sigma-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.round((job.progress.currentPage / job.progress.totalPages) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Page {job.progress.currentPage} of {job.progress.totalPages}
              </p>
            </div>
          )}

          {/* Error message for failed jobs */}
          {job.status === 'failed' && job.error && (
            <p className="text-xs text-red-500 mt-1 truncate flex items-center">
              <XCircle className="w-3 h-3 mr-1 flex-shrink-0" />
              {job.error}
            </p>
          )}
          {moderationError && (
            <p className="mt-1 flex items-center text-xs text-red-500">
              <XCircle className="mr-1 h-3 w-3 flex-shrink-0" />
              {moderationError}
            </p>
          )}
        </div>

        {/* Right: score + status + delete */}
        <div className="flex-shrink-0 flex items-center gap-3">
          {job.status === 'completed' && job.markedPdfPath && job.markingType !== 'ai-collaborative' && (
            <button
              type="button"
              onClick={handleCreateModeration}
              disabled={isCreatingModeration}
              className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-wait disabled:opacity-60"
              title="Open the marked script as a new remarking or moderation job"
            >
              <RotateCcw className={`mr-1 h-3.5 w-3.5 ${isCreatingModeration ? 'animate-spin' : ''}`} />
              {isCreatingModeration ? 'Preparing...' : 'Remark / Moderate'}
            </button>
          )}

          {/* Score badge */}
          {results && results.percentage != null && (
            <span className={`inline-flex items-center px-2.5 py-1 text-sm font-bold rounded-lg ring-1 ${getScoreColor(results.percentage)}`}>
              {results.percentage}%
            </span>
          )}

          {/* Status pill */}
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ring-1 ${statusInfo.pill}`}>
            {isActive && (
              <span className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-current mr-1.5" />
            )}
            {job.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
            {job.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
            {statusInfo.label}
          </span>

          {/* Delete button */}
          {confirmDelete ? (
            <div className="flex items-center gap-1" onClick={e => e.preventDefault()}>
              <button
                onClick={handleDelete}
                className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={handleCancelDelete}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleDelete}
              className="p-1.5 text-gray-300 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
              title="Delete job"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

export default MarkingJobCard;
